import React from "react";
import { Spinner } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { useDeleteRole } from "newui/features/RolesPermissions/hooks/useRoles";
import type { RoleResource } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { showToast } from "utils/helpers";

export interface DeleteRoleModalProps {
	isOpen: boolean;
	role: RoleResource | null;
	onClose: () => void;
}

const BASE_ROLE_ERROR_MESSAGE = "System roles can't be deleted.";

export const DeleteRoleModal = ({ isOpen, role, onClose }: DeleteRoleModalProps) => {
	const deleteRoleMutation = useDeleteRole();

	const handleClose = () => {
		deleteRoleMutation.reset();
		onClose();
	};

	const handleConfirm = () => {
		if (!role) {
			return;
		}

		deleteRoleMutation.mutate(
			{ roleId: role.id },
			{
				onSuccess: () => {
					showToast(`"${role.name}" has been deleted`, "Role deleted", true, "delete-role-success");
					handleClose();
				},
				onError: (error: unknown) => {
					const status = (error as { response?: { status?: number } })?.response?.status;

					if (status === 403) {
						showToast(BASE_ROLE_ERROR_MESSAGE, "Delete failed", false, "delete-role-403");
						return;
					}

					showToast("Unable to delete this role. Please try again.", "Delete failed", false, "delete-role-error");
				},
			}
		);
	};

	const FooterComponent = () => {
		return (
			<div className="flex w-full justify-between gap-2">
				<Button
					onClick={handleClose}
					btntype="base"
					className="!w-full"
					disabled={deleteRoleMutation.isPending}
					data-testid="cancel-delete-role-button"
				>
					Cancel
				</Button>
				<Button
					className="!w-full"
					btntype="danger"
					onClick={handleConfirm}
					disabled={deleteRoleMutation.isPending}
					data-testid="confirm-delete-role-button"
				>
					{deleteRoleMutation.isPending ? <Spinner size="sm" /> : "Delete"}
				</Button>
			</div>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Delete Role"
			description={role ? `Are you sure you want to delete "${role.name}"? This cannot be undone.` : undefined}
			iconType="warning"
			titleColor="red.600"
			size="md"
			customFooter={<FooterComponent />}
		>
			<div data-testid="delete-role-modal-content" />
		</Modal>
	);
};
